import { useQueries, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api, type ProjectBudgetStatus } from './api'
import { hasBudgetData, pickDefaultProjectId } from './budget-view'

/**
 * Budget status of every visible project plus the default selection.
 * Always uses the session user, independently of calendar View as.
 *
 * Only the selected project polls (every 30 s, other users may consume
 * the contingent); the others are refreshed via invalidateEntryData /
 * invalidateAbsenceData and on remount. The "last booked" project comes
 * from the shared last-selection query, not from the full entry list.
 */
export function useBudgetProjects(
  date: string,
  preferredId: string | null,
  getStatus: (projectId: string, date: string) => Promise<ProjectBudgetStatus>,
) {
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const userId = me.data?.user.id
  const projects = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects(),
    enabled: !!userId,
  })
  const lastSelection = useQuery({
    queryKey: ['time-entries', 'last-selection'],
    queryFn: () => api.getLastSelection(),
    enabled: !!userId,
    staleTime: 60_000,
  })
  const [pollingId, setPollingId] = useState<string | null>(null)
  const list = projects.data?.projects ?? []
  const results = useQueries({
    queries: list.map((project) => ({
      queryKey: ['project-budget-status', project.id, { date, userId }],
      queryFn: () => getStatus(project.id, date),
      enabled: !!userId,
      retry: false,
      staleTime: 30_000,
      refetchInterval: project.id === pollingId ? 30_000 : false,
    })),
  })
  const statusByProject: Record<string, ProjectBudgetStatus | undefined> = {}
  list.forEach((project, index) => {
    statusByProject[project.id] = results[index].isError ? undefined : results[index].data
  })
  const selectedId =
    preferredId && list.some((p) => p.id === preferredId)
      ? preferredId
      : pickDefaultProjectId(lastSelection.data?.projectId, statusByProject)
  useEffect(() => setPollingId(selectedId), [selectedId])
  const sortedProjects = [...list].sort(
    (a, b) =>
      Number(hasBudgetData(statusByProject[b.id])) - Number(hasBudgetData(statusByProject[a.id])),
  )
  return {
    projects: sortedProjects,
    statusByProject,
    selectedId,
    status: selectedId ? statusByProject[selectedId] : undefined,
    hasActivePeriod: results.some((r) => !r.isError && hasBudgetData(r.data)),
    isLoading:
      me.isLoading ||
      projects.isLoading ||
      lastSelection.isLoading ||
      results.some((r) => r.isLoading),
    isError: me.isError || projects.isError || results.some((r) => r.isError),
  }
}
